import { apiClient } from "@/shared/services/api";
import type { ApiResponse } from "@/features/auth/services/authApi";

export interface FieldOversightLatestCheckIn {
  userId: string;
  agronomistName: string;
  phoneNumber?: string | null;
  lastCheckedInAt?: string | null;
  lastLocationName?: string | null;
  lastLatitude?: number | null;
  lastLongitude?: number | null;
  lastActivityStatus?: "FieldWork" | "FarmerVisit" | "InTransit" | "OfficeAdmin" | "Other" | null;
  lastFarmerOrFarmName?: string | null;
  lastPromptedAt?: string | null;
  lastRespondedAt?: string | null;
  lastPromptMissed: boolean;
  pollingIntervalMinutes: number;
  minutesSinceLastCheckIn?: number | null;
  isOverdue: boolean;
}

export interface FieldOversightMissedCheckInItem {
  userId: string;
  agronomistName: string;
  missedCount: number;
  lastMissedAt?: string | null;
}

export interface FieldOversightMissedCheckInReport {
  fromUtc: string;
  toUtc: string;
  totalMissedPrompts: number;
  distinctAgronomists: number;
  items: FieldOversightMissedCheckInItem[];
}

export interface FieldOversightAgronomistOption {
  userId: string;
  agronomistName: string;
}

export interface FieldOversightReportConfiguration {
  recipientEmails: string;
  frequency: "daily" | "weekly" | "off";
  deliveryTime: string;
}

export const fieldOversightApi = {
  getLatestCheckIns: async (q?: string): Promise<FieldOversightLatestCheckIn[]> => {
    const { data } = await apiClient.get<ApiResponse<FieldOversightLatestCheckIn[]>>(
      "/field-oversight/latest-check-ins",
      { params: q ? { q } : undefined }
    );
    return data.data;
  },

  getMissedCheckInReport: async (params?: {
    fromUtc?: string;
    toUtc?: string;
    agronomistUserId?: string;
  }): Promise<FieldOversightMissedCheckInReport> => {
    const { data } = await apiClient.get<ApiResponse<FieldOversightMissedCheckInReport>>(
      "/field-oversight/missed-check-ins",
      { params }
    );
    return data.data;
  },

  getAgronomists: async (): Promise<FieldOversightAgronomistOption[]> => {
    const { data } = await apiClient.get<ApiResponse<FieldOversightAgronomistOption[]>>(
      "/field-oversight/agronomists"
    );
    return data.data;
  },

  getReportConfiguration: async (): Promise<FieldOversightReportConfiguration> => {
    const { data } = await apiClient.get<ApiResponse<FieldOversightReportConfiguration>>(
      "/field-oversight/report-configuration"
    );
    return data.data;
  },

  updateReportConfiguration: async (
    configuration: FieldOversightReportConfiguration
  ): Promise<FieldOversightReportConfiguration> => {
    const { data } = await apiClient.put<ApiResponse<FieldOversightReportConfiguration>>(
      "/field-oversight/report-configuration",
      configuration
    );
    return data.data;
  },
};
